import { useEffect, useRef, useState, useCallback } from 'react';
import { Grid } from '@mui/material';
import { useAppSelector, useAppDispatch } from 'src/hooks';
import TableComponent from 'src/components/collapsible-table/table';
import { getTableColumnData } from 'src/components/collapsible-table/table-column-prep';
import _ from 'lodash';
import { useReactTable } from '@tanstack/react-table';
import {
  ExpandedState,
  getCoreRowModel,
  getExpandedRowModel,
  RowSelectionState
} from '@tanstack/table-core';
import { TableComponent as TableType } from '../pricing-simulator/types/generic-table.model';
import TableSkelatonContainer from '../pricing-simulator/components/simulation-table-container/table-skeleton';
import { getFilteredRows } from '../scenario-manager/scenario-manager-util';
import { 
  setSourceSelectionState, 
  setSourceSelectedRows 
} from './store/cluster-summary-slice';

interface IClusterSummaryTableData {
  onRowSelectionChange: (
    selectedRows: { [key: string]: string | number }[]
  ) => void;
}

export default function ClusterSummaryTableData({
  onRowSelectionChange
}: IClusterSummaryTableData){
    const appDispatch = useAppDispatch();
    
    const {
      sourceScenarioMap,
      selectedSourceKey,
      fetchingTableData,
      inputCols,
      isDeletingScenario,
      sourceSelectionMap,
      sourceSelectedRowsMap
    } = useAppSelector((state) => state.clusterSummary);
    
    const { activeCheckboxValues, searchInputVal } = useAppSelector(
      (state) => state.scenarioManagerTopBar
    );
    
    const [columns, setColumns] = useState([]);
    
    // Get the selection state for the current source, default to empty if not found
    const currentSourceSelectionState = sourceSelectionMap[selectedSourceKey] || {};
    const [rowSelection, setRowSelection] = useState<RowSelectionState>(currentSourceSelectionState);
    
    const rawRows = sourceScenarioMap?.[selectedSourceKey] ?? [];
    const [rows, setRows] = useState(
      getFilteredRows(
        rawRows,
        searchInputVal,
        activeCheckboxValues
      ).sort(
        (a, b) =>
          new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
      )
    );
    const [expanded, setExpanded] = useState<ExpandedState>({});
    const tableRef = useRef<HTMLTableElement>();
    
    const table = useReactTable({
      data: rows,
      columns,
      state: {
        expanded,
        rowSelection
      },
      onExpandedChange: setExpanded,
      onRowSelectionChange: setRowSelection,
      getSubRows: (row: any) => row.subRows,
      getCoreRowModel: getCoreRowModel(),
      getExpandedRowModel: getExpandedRowModel()
    });

    // Handle selection changes
    useEffect(() => {
      const timeoutId = setTimeout(() => {
        const selectedRows = table.getSelectedRowModel().flatRows.map((row) => row.original);
        
        // Only update Redux if the selection actually changed
        const currentStoredSelection = sourceSelectionMap[selectedSourceKey] || {};
        const currentStoredRows = sourceSelectedRowsMap[selectedSourceKey] || [];
        
        // Check if selection state has actually changed
        const selectionChanged = JSON.stringify(currentStoredSelection) !== JSON.stringify(rowSelection);
        const rowsChanged = selectedRows.length !== currentStoredRows.length || 
          JSON.stringify(selectedRows.map(r => r.scenario_id)) !== JSON.stringify(currentStoredRows.map(r => r.scenario_id));
        
        if (selectionChanged || rowsChanged) {
          // Store selection state and selected rows in Redux for current source
          appDispatch(setSourceSelectionState({
            sourceId: selectedSourceKey,
            selectionState: rowSelection
          }));
          
          appDispatch(setSourceSelectedRows({
            sourceId: selectedSourceKey,
            selectedRows: selectedRows
          }));
        }
        onRowSelectionChange(selectedRows);
      }, 100); // Debounce to prevent rapid updates

      return () => clearTimeout(timeoutId);
    }, [rowSelection, selectedSourceKey]);

    // Update selection state when selectedSourceKey changes
    useEffect(() => {
      const currentSelectionState = sourceSelectionMap[selectedSourceKey] || {};
      
      // Only update if the selection state is actually different
      if (JSON.stringify(currentSelectionState) !== JSON.stringify(rowSelection)) {
        setRowSelection(currentSelectionState);
      }
    }, [selectedSourceKey]);

    // Update rows when filters or source changes
    useEffect(() => {
      setRows(
        getFilteredRows(
          rawRows,
          searchInputVal,
          activeCheckboxValues
        ).sort(
          (a, b) =>
            new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
        )
      );
      if (isDeletingScenario) {
        const currentSelectionState = sourceSelectionMap[selectedSourceKey] || {};
        setRowSelection(currentSelectionState);
      }
    }, [
      selectedSourceKey, 
      sourceScenarioMap, 
      activeCheckboxValues,
      isDeletingScenario,
      searchInputVal,
      rawRows,
    ]);

    useEffect(() => {
      setColumns([...getTableColumnData(inputCols, TableType.SCENARIOMANAGER, [])]);
    }, [inputCols, selectedSourceKey]);


    return (
      <Grid container>
        {(fetchingTableData || !columns.length) && (
          <Grid item xs={12}>
            <TableSkelatonContainer rowLength={10} headerLength={6} />
          </Grid>
        )}

        {/* Show "No scenario present" if there are no rows */}
        {!fetchingTableData && rows.length === 0 && (
          <Grid item xs={12}>
            <div style={{ padding: '32px 0', textAlign: 'center', width: '100%' }}>
              <span style={{ color: '#000000ff', fontWeight: 500, fontSize: 18 }}>
                No scenario present
              </span>
            </div>
          </Grid>
        )}
        
        {!fetchingTableData && rows.length!=0 &&(
          <Grid item xs={12}>
            {/* Use the table component to render the rows */}
            <TableComponent
              setHeightExplicitly
              table={table}
              extClassName='scenario-manager-table'
              tableRef={tableRef}
              isRowSelect
              isMultiple={false}
            />
          </Grid>
        )}
      </Grid>
    );
}